import { Design } from './design/design'

import * as def from '@/definitions'
import { Rectangle } from '@/utilityModels/rectangle';
import { MapHelper } from '@/helper/mapHelper';
import { Frame } from '../../frame/frame';
import { MathHelper } from '@/helper/mathHelper';

// 面
export class Surface
{
    // サイズ
    public width : number = 0;
    public height : number = 0;
    // フレーム配置情報
    public frameArray : Array<Frame> = new Array();
    // 裏面フラグ
    private isReverse ; boolean = false;

    // デザイン 
    // デザインの存在しない位置があるため、配列ではなく連想配列
    private _designMap : { [position : number] : Design } = {};
    public get designMap() { return this._designMap; }
    public set designMap(designMap) { this._designMap = designMap; }
    public getDesign(position)
    {
        if(this._designMap[position]) { return this._designMap[position]; }
        return null;
    }
    public setDesign(position, design) { this._designMap = MapHelper.addOrReplace(this._designMap, position, design); }
    public removeDesign(position) { this._designMap = MapHelper.removeKey(this._designMap, position); }

    // デザイン印刷可能情報配列
    private _printableArray : Array<boolean> = new Array();
    public get printableArray() { return this._printableArray; }
    public set printableArray(printableArray) { this._printableArray = printableArray; }
    public isPrintable(position : number)
    {
        if(0 <= position && position < this._printableArray.length)
        {
            return this._printableArray[position];
        }
        // 情報がない場合は印刷可能
        return true;
    }
    public setPrintable(position : number, printable : boolean)
    {
        var newArray = new Array();
        var maxSize = Math.max(position + 1, this._printableArray.length);
        for(var i = 0; i < maxSize; ++i)
        {
            newArray.push(true);

            if(i < this._printableArray.length)
            {
                newArray[i] = this._printableArray[i];
            }
        }
        newArray[position] = printable;

        this._printableArray = newArray;
    }

    // デザイン配置領域
    // キャンバスサイズに使用する
    public getDesignRect(position : number) : Rectangle
    {
        var array = this.getDesignRectArray();
        if(position < 0 || array.length <= position) { return new Rectangle(); }
        return array[position];
    }

    // フレーム丸み
    public getFrameRadius(position : number) : number
    {
        var array = this.getFrameRadiusArray();
        if(position < 0 || array.length <= position) { return 0; }
        return array[position];
    }

    // フレーム画像(base64)
    public getFrameImage(position : number) : string
    {
        var array = this.getFrameImageArray();
        if(position < 0 || array.length <= position) { return ""; }
        return array[position];
    }

    // 内側の円の半径(ない場合は0)
    public getFrameInnerCircleRadius(position : number) : number
    {
        var array = this.getFrameInnerCircleRadiusArray();
        if(position < 0 || array.length <= position) { return 0; }
        return array[position];
    }

    // 塗り足し領域
    // bleedSize ... 塗り足しとして確保したい領域。
    // *実際に確保できる塗り足し領域は、用紙外・隣りあったデザイン領域にはみ出さないように設定される。
    public getBleedRect(position, bleedSize : number = 3.0) : Rectangle
    {
        var array = this.getDesignRectArray();
        if(position < 0 || array.length <= position) { return new Rectangle(); }
        var designRect = array[position];

        // 所属するフレーム、row,colを求める
        var pos = 0;
        var row = 0;
        var col = 0
        var frameIndex = 0;
        for(var i = 0; i < this.frameArray.length; ++i)
        {
            var frame = this.frameArray[i];
            for(var r = 0; r < frame.row; ++r)
            {
                for(var c = 0; c < frame.col; ++c)
                {
                    if(pos == position)
                    {
                        row = r;
                        col = c;
                        frameIndex = i;
                    }
                    pos++;
                }
            }
        }

        // 各辺について、塗り足し幅の限界
        var currentFrame = this.frameArray[frameIndex];
        var leftBleedMax = 0;
        var topBleedMax = 0;
        var rightBleedMax = 0;
        var bottomBleedMax = 0;
        var isLeft = this.isReverse ? col == currentFrame.col - 1 : col == 0;
        var isRight = this.isReverse ? col == 0 : col == currentFrame.col - 1;
        leftBleedMax = isLeft ? currentFrame.leftMargin : currentFrame.horizontalPadding / 2;
        topBleedMax = (row == 0) ? currentFrame.topMargin : currentFrame.verticalPadding / 2;
        rightBleedMax = isRight ? this.width - designRect.right : currentFrame.horizontalPadding / 2;
        bottomBleedMax = (row == currentFrame.row - 1) ? this.height - designRect.bottom : currentFrame.verticalPadding / 2;
        // 限界値と設定値の小さい方を採用
        var leftBleed = Math.min(leftBleedMax, bleedSize);
        var topBleed = Math.min(topBleedMax, bleedSize);
        var rightBleed = Math.min(rightBleedMax, bleedSize);
        var bottomBleed = Math.min(bottomBleedMax, bleedSize)
        
        var bleedRect = new Rectangle(designRect.x - leftBleed, designRect.y - topBleed, designRect.width + leftBleed + rightBleed, designRect.height + topBleed + bottomBleed);
        return bleedRect;
    }

    private getDesignRectArray() : Array<Rectangle>
    {
        var array = new Array();
        for(var i = 0; i < this.frameArray.length; ++i)
        {
            var frame = this.frameArray[i];
            var x = this.isReverse ? this.width - frame.leftMargin - frame.width : frame.leftMargin;
            var y = frame.topMargin;
            for(var r = 0; r < frame.row; ++r)
            {
                for(var c = 0; c < frame.col; ++c)
                {
                    array.push(new Rectangle(x, y , frame.width, frame.height));

                    var dx = frame.width + frame.horizontalPadding;
                    x += this.isReverse ? -dx : dx;
                }
                x = this.isReverse ? this.width - frame.leftMargin - frame.width : frame.leftMargin;
                y += frame.height + frame.verticalPadding;
            }
        }

        return array;
    }

    private getFrameImageArray() : Array<string>
    {
        var array = new Array();
        for(var i = 0; i < this.frameArray.length; ++i)
        {
            var frame = this.frameArray[i];
            for(var r = 0; r < frame.row; ++r)
            {
                for(var c = 0; c < frame.col; ++c)
                {
                    if(frame.shape != null)
                    {
                        if(this.isReverse)
                        {
                            array.push(frame.shape.reverseImage);
                        }
                        else
                        {
                            array.push(frame.shape.faceImage);
                        }
                    }
                    else
                    {
                        array.push("");
                    }
                }
            }
        }

        return array;
    }

    private getFrameRadiusArray() : Array<number>
    {
        var array = new Array();
        for(var i = 0; i < this.frameArray.length; ++i)
        {
            var frame = this.frameArray[i];
            for(var r = 0; r < frame.row; ++r)
            {
                for(var c = 0; c < frame.col; ++c)
                {
                    if(frame.shape)
                    {
                        array.push(frame.shape.cornerRadius);
                    }
                    else
                    {
                        array.push(0);
                    }
                }
            }
        }

        return array;
    }

    private getFrameInnerCircleRadiusArray() : Array<number>
    {
        var array = new Array();
        for(var i = 0; i < this.frameArray.length; ++i)
        {
            var frame = this.frameArray[i];
            for(var r = 0; r < frame.row; ++r)
            {
                for(var c = 0; c < frame.col; ++c)
                {
                    if(frame.shape)
                    {
                        array.push(frame.shape.type == 1 ? frame.shape.innerCircleRadius : 0);
                    }
                    else
                    {
                        array.push(0);
                    }
                }
            }
        }

        return array;
    }

    // 新しいデザインを作成する
    public createNewDesign(position : number) : Design
    {
        // 領域を取得
        var designRect = this.getDesignRect(position);
        
        // 領域のサイズを元にデザインを作成
        var design = Design.create(designRect.width, designRect.height);
        
        // デザインを追加する
        this.setDesign(position, design);

        return design;
    }

    // デザインの複製
    // position : 複製元
    // isHorizontal : 横方向に
    // isVertical : 縦方向に
    public duplicateDesign(position : number, isHorizontal : boolean, isVertical : boolean)
    {
        var checkPosition = 0;
        var frameIndex = 0;
        var targetRow = 0;
        var targetColumn = 0;
        for(var i = 0; i < this.frameArray.length; ++i)
        {
            var frame = this.frameArray[i];
            
            for(var r = 0; r < frame.row; ++r)
            {
                for(var c = 0; c < frame.col; ++c)
                {
                    if(checkPosition == position)
                    {
                        frameIndex = i;
                        targetRow = r;
                        targetColumn = c;
                        break;
                    }
                    checkPosition++;
                }
            }
        }

        if(frameIndex >= this.frameArray.length) { return; }
        
        var targetFrame = this.frameArray[frameIndex];

        // コピー元のデザイン
        var design = this.getDesign(position);
        if(isHorizontal)
        {
            // 横方向に複製
            for(var c = 0; c < targetFrame.col; ++c)
            {
                if(c == targetColumn) { continue; }
                var toPosition = position - targetColumn + c;
                if(design != null)
                {
                    this.setDesign(toPosition, design.clone());
                }
                else
                {
                    this.removeDesign(toPosition);
                }
            }
        }
        if(isVertical)
        {
            // 縦方向に複製
            for(var r = 0; r < targetFrame.row; ++r)
            {
                if(r == targetRow) { continue; }
                var toPosition = position - targetRow * targetFrame.col + r * targetFrame.col;
                if(design != null)
                {
                    this.setDesign(toPosition, design.clone());
                }
                else
                {
                    this.removeDesign(toPosition);
                } 
            }
        }
    }

    public static create(width : number, height : number, frameArray : Array<Frame>, isReverse : boolean = false, rotate = 0)
    {
        var surface = new Surface()

        surface.width = width;
        surface.height = height;
        surface.isReverse = isReverse;
        surface.frameArray = frameArray;
        
        return surface;
    }

    // JSON
    public toJSON()
    {
        // JSONパース用オブジェクト
        var cloneObj = {
            width : this.width,
            height : this.height,
            isReverse : this.isReverse,
            frameArray : this.frameArray,
            designMap : this.designMap,
            printableArray : this.printableArray
        };

        return cloneObj;
    }

    public static createFromJsonData(jsonData : any)
    {
        var surface = new Surface();
        if(jsonData.designMap)
        {
            for(var position in jsonData.designMap)
            {
                var design = Design.createFromJsonData(jsonData.designMap[position]);
                surface.setDesign(position, design);
            }
        }
        if(jsonData.frameArray)
        {
            for(var frameData of jsonData.frameArray)
            {
                surface.frameArray.push(Frame.createFromJsonData(frameData));
            }
        }
        if(jsonData.printableArray) { surface.printableArray = jsonData.printableArray; }
        if(jsonData.width) { surface.width = jsonData.width; }
        if(jsonData.height) { surface.height = jsonData.height; }
        if(jsonData.isReverse) { surface.isReverse = jsonData.isReverse; }

        return surface;
    }

    // エラーチェック
    public hasError()
    {
        var error = false;
        for(var position in this.designMap)
        {
            var design = this.designMap[position];
            if(design.hasError())
            {
                error = true;
                break;
            }
        }

        return error;
    }
}
